class HHVariantPicker extends HTMLElement {
  constructor() {
    super()
    this.onCartUpdated = this.onCartUpdated.bind(this)
    this.currentVariant = null
    this.isInternational = false
  }

  connectedCallback() {
    // Check if customer is international (non-US)
    this.isInternational = this.dataset.isInternational === 'true'

    if (this.dataset.selectedVariant) {
      this.currentVariant = this.getVariantData().find((variant) => {
        return variant.id == this.dataset.selectedVariant
      })
    }
    this.addEventListener('change', this.onVariantChange)
    this.initDisabledVariants()
    this.getProductData()
    document.addEventListener('cart:updated', this.onCartUpdated)
  }

  disconnnectedCallback() {
    this.removeEventListener('change', this.onVariantChange)
    document.removeEventListener('cart:updated', this.onCartUpdated)
  }

  async getCartData() {
    try {
      let response = await fetch('/cart.js')
      if (response.ok) {
        this.cart = await response.json()
        return this.cart
      }
    } catch (error) {
      console.error('Error fetching cart:', error)
    }
  }

  getProductData() {
    this.product = JSON.parse(
      document.querySelector('[data-product-data]')?.textContent || '{}'
    )
  }

  initDisabledVariants() {
    this.getVariantData().forEach((variant) => {
      if (!variant.available) {
        document
          .querySelector(
            `.js-variant-label[data-option-value='${variant.title}']`
          )
          ?.classList.add('c-subdued')
      } else {
        document
          .querySelector(
            `.js-variant-label[data-option-value='${variant.title}']`
          )
          ?.classList.add('bold')
      }
    })
  }

  onVariantChange() {
    this.updateOptions()
    this.updateMasterId()
    this.toggleAddButton(true, '', false)

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true)
      this.setUnavailable()
    } else {
      this.updateMedia()
      this.updateURL()
      this.updateVariantInput()
      this.updatePreorderState()
      this.renderProductInfo()
      this.getCartData().then(() => {
        this.validateQuantity()
        this.triggerSizekick()
      })
    }
  }

  /**
   * Check if the current variant is a preorder variant
   * @returns {boolean}
   */
  isVariantPreorder() {
    if (!this.currentVariant) return false
    // Check variant-level is_preorder flag from metafields
    return this.currentVariant.is_preorder === true
  }

  /**
   * Update preorder state based on selected variant
   * - Updates form properties for international customers
   * - Updates button text
   * - Shows/hides dynamic checkout
   * - Updates Submarine block visibility
   */
  updatePreorderState() {
    const isPreorder = this.isVariantPreorder()
    const form = document.getElementById(`product-form-${this.dataset.section}`)
    if (!form) return

    // Update preorder property input
    const preorderInput = form.querySelector('[data-preorder-property]')
    if (preorderInput) {
      if (isPreorder) {
        preorderInput.removeAttribute('disabled')
      } else {
        preorderInput.setAttribute('disabled', '')
      }
    }

    // Update preorder message input (for international customers)
    const preorderMessageInput = form.querySelector('[data-preorder-message]')
    if (preorderMessageInput) {
      if (
        isPreorder &&
        this.isInternational &&
        this.currentVariant.preorder_message
      ) {
        preorderMessageInput.value = this.currentVariant.preorder_message
        preorderMessageInput.removeAttribute('disabled')
      } else {
        preorderMessageInput.setAttribute('disabled', '')
      }
    }

    // Update the data attribute on this element for toggleAddButton
    if (isPreorder) {
      this.setAttribute('data-pre-order', 'true')
      this.dataset.preOrder = 'true'
    } else {
      this.removeAttribute('data-pre-order')
      delete this.dataset.preOrder
    }

    // Update add button data attribute
    const addButton = form.querySelector('[name="add"]')
    if (addButton) {
      addButton.setAttribute('data-is-preorder', isPreorder.toString())
    }

    // Show/hide dynamic checkout button (hide for preorder)
    const dynamicCheckout = form.querySelector('[data-dynamic-checkout]')
    if (dynamicCheckout) {
      dynamicCheckout.style.display = isPreorder ? 'none' : ''
    }

    // Disable/enable any selling_plan inputs injected by Submarine
    // This prevents selling_plan from being submitted when variant is not preorder
    const sellingPlanInputs = form.querySelectorAll(
      '[name="selling_plan"], [name*="selling_plan"]'
    )
    sellingPlanInputs.forEach((input) => {
      if (isPreorder) {
        input.removeAttribute('disabled')
      } else {
        input.setAttribute('disabled', '')
      }
    })

    // Update Submarine presale selector visibility based on variant and location
    this.updateSubmarineBlockVisibility(isPreorder)

    // Dispatch event for other components that might need to react
    document.dispatchEvent(
      new CustomEvent('variant:preorder-changed', {
        bubbles: true,
        detail: {
          variantId: this.currentVariant.id,
          isPreorder: isPreorder,
          isInternational: this.isInternational,
          preorderMessage: this.currentVariant.preorder_message,
          preorderDescription: this.currentVariant.preorder_description,
        },
      })
    )
  }

  /**
   * Update Submarine block visibility based on variant preorder status
   * - US customers with preorder variant: Show Submarine block
   * - International customers with preorder variant: Show description text
   * - Non-preorder variant: Hide both
   */
  updateSubmarineBlockVisibility(isPreorder) {
    const submarineBlockWrapper = document.querySelector(
      '[data-submarine-block]'
    )
    const presaleDescription = document.querySelector(
      '[data-presale-description]'
    )

    if (this.isInternational) {
      // International: Hide Submarine app block, show/update description
      if (submarineBlockWrapper) {
        submarineBlockWrapper.style.display = 'none'
      }
      if (presaleDescription) {
        if (isPreorder && this.currentVariant.preorder_description) {
          presaleDescription.textContent =
            this.currentVariant.preorder_description
          presaleDescription.style.display = ''
        } else {
          presaleDescription.style.display = 'none'
        }
      }
    } else {
      // US: Show/hide Submarine app block based on preorder status
      if (submarineBlockWrapper) {
        submarineBlockWrapper.style.display = isPreorder ? '' : 'none'
      }
      if (presaleDescription) {
        presaleDescription.style.display = 'none'
      }
    }
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'))
    if (fieldsets && fieldsets.length) {
      this.options = fieldsets.map((fieldset) => {
        return Array.from(fieldset.querySelectorAll('input')).find(
          (radio) => radio.checked
        ).value
      })
    } else {
      this.options = Array.from(
        this.querySelectorAll('select'),
        (select) => select.value
      )
    }
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option
        })
        .includes(false)
    })
    this.setAttribute('data-selected-variant', this.currentVariant.id)
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant?.featured_media) return
    const newMedia = document.querySelector(
      `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
    )
    if (!newMedia) return
    const parent = newMedia.parentElement
    parent.prepend(newMedia)
    window.setTimeout(() => {
      parent.scroll(0, 0)
    })
  }

  updateURL() {
    if (!this.currentVariant) return
    window.history.replaceState(
      {},
      '',
      `${this.dataset.url}?variant=${this.currentVariant.id}`
    )
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment`
    )
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]')
      input.value = this.currentVariant.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }
  validateQuantity() {
    let check_against_inventory = true
    let quantity_rule_soldout = false
    this.qtyMessageContrainer = document.querySelector('.quantity-rule-soldout')

    const cartQuantity =
      this.cart?.items.find(
        (item) => item.variant_id === this.currentVariant.id
      )?.quantity || 0

    const inventoryQuantity = this.currentVariant.inventory_quantity
    if (
      this.currentVariant.inventory_management != 'shopify' ||
      this.currentVariant.inventory_policy == 'continue'
    ) {
      check_against_inventory = false
    }
    if (
      this.currentVariant.quantity_rule.min + cartQuantity >
        inventoryQuantity &&
      check_against_inventory
    ) {
      quantity_rule_soldout = true
    }

    if (quantity_rule_soldout || !this.currentVariant.available) {
      this.toggleAddButton(
        true,
        this.currentVariant.available
          ? window.variantStrings.addToCart
          : window.variantStrings.soldOut,
        false,
        !this.currentVariant.available
      )

      if (this.currentVariant.available) {
        this.qtyMessageContrainer?.classList.add('block')
        this.qtyMessageContrainer?.classList.remove('hidden')
      } else {
        this.qtyMessageContrainer?.classList.add('hidden')
        this.qtyMessageContrainer?.classList.remove('block')
      }
    } else {
      this.qtyMessageContrainer?.classList.add('hidden')
      this.qtyMessageContrainer?.classList.remove('block')
      this.toggleAddButton(!this.currentVariant.available)
    }
  }

  async onCartUpdated() {
    if (this.currentVariant) {
      this.getCartData().then(() => {
        this.validateQuantity()
      })
    }
  }

  renderProductInfo() {
    fetch(
      `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`
    )
      .then((response) => response.text())
      .then((responseText) => {
        const id = `price-${this.dataset.section}`
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const destination = document.getElementById(id)
        const source = html.getElementById(id)

        if (source && destination) destination.innerHTML = source.innerHTML

        document
          .getElementById(`price-${this.dataset.section}`)
          ?.classList.remove('visibility-hidden')
      })
  }

  toggleAddButton(disable = true, text, modifyClass = true, hideOnOOS = true) {
    const addButton = document
      .getElementById(`product-form-${this.dataset.section}`)
      ?.querySelector('[name="add"]')

    // Check variant-level preorder status first, fall back to element attribute
    const isPreorder = this.isVariantPreorder()
    const atcText = isPreorder
      ? window.variantStrings.preOrder
      : this.dataset.finalSale
      ? window.variantStrings.addToCartFinalSale
      : window.variantStrings.addToCart

    if (!addButton) return

    if (disable) {
      addButton.setAttribute('disabled', true)
      addButton.setAttribute('style', 'display: none;')
      if (text) addButton.textContent = text
    } else {
      addButton.removeAttribute('disabled')
      addButton.removeAttribute('style')
      addButton.textContent = atcText
    }

    if (!hideOnOOS) addButton.removeAttribute('style')

    if (!modifyClass) return
  }

  setUnavailable() {
    const addButton = document
      .getElementById(`product-form-${this.dataset.section}`)
      ?.querySelector('[name="add"]')
    if (!addButton) return
    addButton.textContent = window.variantStrings.unavailable
    document
      .getElementById(`price-${this.dataset.section}`)
      ?.classList.add('visibility-hidden')
  }

  getVariantData() {
    this.variantData =
      this.variantData ||
      JSON.parse(this.querySelector('[type="application/json"]').textContent)
    return this.variantData
  }

  async triggerSizekick() {
    this.sizekickTrigger = document.querySelector('hh-sizekick-trigger')
    if (!this.product || !this.sizekickTrigger || !this.cart) return

    const cartItems = this.cart?.items.filter(
      (item) => item.product_id === this.product?.id
    )
    const sizePosition = this.product.options.find(
      (option) => option.name === 'Size' || option.name === 'size'
    )?.position
    // check if cart has similar item but different size
    const similarItem = cartItems.find((item) => {
      return item.options_with_values.some((option) => {
        return (
          option.value !== this.currentVariant[`option${sizePosition}`]?.value
        )
      })
    })
    if (similarItem) {
      this.sizekickTrigger?.showTooltip()
    }
  }
}

customElements.define('hh-variant-picker', HHVariantPicker)
